import React, { useRef, useState } from "react";
import { Mail, ArrowUpRight, Check, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import EditorialHeader from "../compponents/EditorialHeader";
import EditorialFooter from "../compponents/EditorialFooter";
import "./About.css";
import "./ContactUs.css";

const emptyForm = { firstName: "", lastName: "", email: "", message: "" };

export default function ContactUs() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState("idle");
  const submitting = useRef(false);
  const sending = status === "sending";
  const copy = key => t(`contactDesign.${key}`);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(current => ({ ...current, [name]: value }));
    if (status !== "idle") setStatus("idle");
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setStatus("sending");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);
    try {
      const response = await fetch("https://leravyn-backend.onrender.com", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Message request failed");
      const data = await response.json();
      if (!data.success) throw new Error("Message was not accepted");
      setStatus("success");
      setFormData(emptyForm);
    } catch {
      setStatus("error");
    } finally {
      clearTimeout(timeout);
      submitting.current = false;
    }
  };

  return (
    <div className="about-page contact-page">
      <a className="about-skip" href="#contact-main">{t("about.design.skip")}</a>
      <EditorialHeader />
      <main id="contact-main" className="about-wrap">
        <section className="contact-layout" aria-labelledby="contact-title">
          <div className="contact-intro about-hero-copy">
            <p className="about-eyebrow"><span />{copy("eyebrow")}</p>
            <h1 id="contact-title">{copy("title")} <em>{copy("titleAccent")}</em></h1>
            <p className="about-body">{t("text")}</p>
            <p className="about-body">{t("text2")}</p>
            <a className="contact-email" href="mailto:valeria@leravyn.com"><span className="contact-mail-icon"><Mail size={21} strokeWidth={1.3} aria-hidden="true" /></span><span><small>{copy("emailDirect")}</small>valeria@leravyn.com</span><ArrowUpRight size={19} aria-hidden="true" /></a>
            <div className="contact-note"><span className="about-signature">Valeria</span><p>{copy("note")}</p></div>
          </div>

          <div className="contact-form-panel">
            <p className="about-eyebrow">{copy("formEyebrow")}</p>
            <h2 id="contact-form-title">{copy("formTitle")}</h2>
            <p className="contact-form-intro">{copy("formIntro")}</p>
            <form name="contact" onSubmit={handleSubmit} aria-labelledby="contact-form-title" aria-busy={sending}>
              <fieldset disabled={sending}>
                <legend className="contact-sr-only">{copy("formTitle")}</legend>
                <div className="contact-fields">
                  <label htmlFor="contact-firstName">{copy("firstName")}<input id="contact-firstName" name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required /></label>
                  <label htmlFor="contact-lastName">{copy("lastName")}<input id="contact-lastName" name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} required /></label>
                  <label className="contact-field-wide" htmlFor="contact-email">{copy("email")}<input id="contact-email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} required /></label>
                  <label className="contact-field-wide" htmlFor="contact-message">{copy("message")}<textarea id="contact-message" name="message" rows={5} placeholder={copy("messagePlaceholder")} value={formData.message} onChange={handleChange} required /></label>
                </div>
                <button className="about-button contact-submit" type="submit" disabled={sending}>{copy(sending ? "sending" : "send")}{sending ? <LoaderCircle className="contact-spinner" size={19} aria-hidden="true" /> : <ArrowUpRight size={19} aria-hidden="true" />}</button>
              </fieldset>
              <div className={`contact-status contact-status-${status}`} role="status" aria-live="polite" aria-atomic="true">{status === "success" && <><Check size={18} aria-hidden="true" /><span>{copy("success")}</span></>}{status === "error" && <span>{copy("error")} <a href="mailto:valeria@leravyn.com">valeria@leravyn.com</a></span>}</div>
            </form>
          </div>
        </section>
        <section className="contact-next" aria-labelledby="contact-next-title"><p className="about-eyebrow">{copy("nextLabel")}</p><h2 id="contact-next-title">{copy("nextTitle")}</h2><p>{copy("nextText")}</p></section>
      </main>
      <EditorialFooter />
    </div>
  );
}
